<template>
  <div class="page">
    <div class="public-wrap auth-box">
      <p class="step-label">Driver · {{ step }} / 5</p>
      <h1>{{ titles[step - 1] }}</h1>
      <p class="lead">{{ leads[step - 1] }}</p>
      <div id="lilo-recaptcha"></div>
      <v-alert v-if="done" type="success" class="mb-4">Your driver file is in for school review. Keep this phone for the Lilo driver app.</v-alert>
      <v-alert v-if="error" type="error" class="mb-4">{{ error }}</v-alert>

      <v-form v-if="!done && step === 1" @submit.prevent="sendCode">
        <div class="phone-row">
          <span>+251</span>
          <v-text-field v-model="phoneLocal" label="Mobile" outlined dense hide-details :rules="[required]"></v-text-field>
        </div>
        <v-btn color="primary" class="mt-6" type="submit" :loading="busy" depressed block>Send SMS code</v-btn>
      </v-form>

      <div v-else-if="!done && step === 2">
        <otp-boxes v-model="otp" @complete="checkCode" />
      </div>

      <v-form v-else-if="!done && step === 3" ref="youForm" @submit.prevent="saveYou">
        <v-select v-model="prefix" :items="prefixes" label="Prefix" outlined dense :rules="[required]"></v-select>
        <v-text-field v-model="firstName" label="First name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="middleName" label="Middle name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="lastName" label="Last name" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="birthDate" label="Birthday" type="date" outlined dense :rules="[required]"></v-text-field>
        <v-text-field v-model="address" label="Address" outlined dense></v-text-field>
        <v-text-field v-model="email" label="Email (optional)" outlined dense></v-text-field>
        <v-text-field v-model="password" label="App password" type="password" outlined dense :rules="[required, min]"></v-text-field>
        <v-text-field v-model="confirmPassword" label="Confirm password" type="password" outlined dense :rules="[required, min, match]"></v-text-field>
        <region-city-fields
          :region-id.sync="regionId"
          :city-id.sync="cityId"
          :area.sync="area"
          required
        />
        <v-btn color="primary" class="mt-6" type="submit" :loading="busy" depressed block>Continue</v-btn>
      </v-form>

      <div v-else-if="!done && step === 4">
        <v-text-field v-model="idNumber" label="National ID number" outlined dense></v-text-field>
        <label>ID photo</label>
        <input type="file" accept="image/*" @change="onDoc($event, 'id')" />
        <v-text-field v-model="licenseNumber" label="Licence number" outlined dense class="mt-4"></v-text-field>
        <label>Licence photo</label>
        <input type="file" accept="image/*" @change="onDoc($event, 'license')" />
        <v-text-field v-model="plateNumber" label="Vehicle plate" outlined dense class="mt-4"></v-text-field>
        <label>Ownership certificate</label>
        <input type="file" accept="image/*" @change="onDoc($event, 'ownership')" />
        <v-btn color="primary" class="mt-6" :loading="busy" depressed block @click="step = 5">Continue</v-btn>
      </div>

      <v-form v-else-if="!done && step === 5" @submit.prevent="submitDriver">
        <v-text-field v-model="schoolCode" label="School code" outlined dense hint="From the school that will approve you" persistent-hint></v-text-field>
        <v-btn color="primary" class="mt-6" type="submit" :loading="busy" depressed block>Submit for review</v-btn>
      </v-form>

      <p class="mt-6">
        <router-link to="/signin">Sign in</router-link>
        <span class="mx-2">·</span>
        <router-link to="/register">Back</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import PhoneAuth from '@/services/PhoneAuthService'
import OtpBoxes from '@/components/OtpBoxes.vue'
import RegionCityFields from '@/components/RegionCityFields.vue'

export default {
  components: { OtpBoxes, RegionCityFields },
  data() {
    return {
      step: 1,
      busy: false,
      done: false,
      error: '',
      session: null,
      phoneLocal: '',
      otp: '',
      prefix: 'Mr',
      prefixes: ['Mr', 'Mrs', 'Ms', 'Dr'],
      firstName: '',
      middleName: '',
      lastName: '',
      birthDate: '',
      address: '',
      email: '',
      password: '',
      confirmPassword: '',
      regionId: null,
      cityId: null,
      area: '',
      idNumber: '',
      licenseNumber: '',
      plateNumber: '',
      schoolCode: '',
      docs: { id: null, license: null, ownership: null },
      titles: ['Your phone', 'SMS code', 'Your details', 'Vehicle papers', 'School'],
      leads: [
        'Driver registration starts with a mobile number and an SMS code.',
        'Enter the 6-digit SMS code. Verification starts when the last digit is in.',
        'Email is optional. This phone is your sign-in.',
        'ID, licence, plate, and ownership certificate.',
        'The school reviews your file before you run trips.',
      ],
    }
  },
  computed: {
    required() { return (v) => !!v || 'Required' },
    min() { return (v) => !v || v.length >= 8 || 'At least 8 characters' },
    match() { return (v) => v === this.password || 'Passwords do not match' },
  },
  methods: {
    async sendCode() {
      this.error = ''
      this.busy = true
      try {
        this.session = await PhoneAuth.sendOtp(this.phoneLocal, 'lilo-recaptcha')
        this.otp = ''
        this.step = 2
      } catch (e) {
        this.error = e.message
      } finally {
        this.busy = false
      }
    },
    async checkCode(code) {
      const otp = String(code || this.otp || '')
      if (this.busy || otp.length !== 6) return
      this.busy = true
      this.error = ''
      try {
        await PhoneAuth.confirmOtp(this.session, otp)
        this.step = 3
      } catch (e) {
        this.error = e.message
      } finally {
        this.busy = false
      }
    },
    async saveYou() {
      if (this.$refs.youForm && !this.$refs.youForm.validate()) return
      this.busy = true
      this.error = ''
      try {
        const res = await axios.post('/auth/createDriver', {
          name: [this.prefix, this.firstName, this.middleName, this.lastName].filter(Boolean).join(' '),
          token: 'phone',
          device_name: 'Lilo-web',
          password: this.password,
          phone: PhoneAuth.digits(this.phoneLocal),
          email: this.email || undefined,
          prefix: this.prefix,
          first_name: this.firstName,
          middle_name: this.middleName,
          last_name: this.lastName,
          birth_date: this.birthDate,
          region_id: this.regionId,
          city_id: this.cityId,
          area: this.area,
          address: this.address,
        })
        PhoneAuth.storeToken(res.data.token)
        this.step = 4
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.message || (e.response.data.errors && (e.response.data.errors.authentication || Object.values(e.response.data.errors)[0])))) || e.message
        if (Array.isArray(this.error)) this.error = this.error[0]
      } finally {
        this.busy = false
      }
    },
    onDoc(event, key) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => { this.docs[key] = reader.result }
      reader.readAsDataURL(file)
    },
    nextYear() {
      const d = new Date()
      d.setFullYear(d.getFullYear() + 2)
      return d.toISOString().slice(0, 10)
    },
    async submitDriver() {
      this.busy = true
      this.error = ''
      try {
        const school = await axios.get(`/users/get-school-by-code/${this.schoolCode}`)
        const schoolId = school.data.school.id
        const documents = []
        const push = (name, number, image) => {
          if (!image) return
          documents.push({
            document_name: name,
            document_number: number || name,
            expiry_date: this.nextYear(),
            document_image: image,
            local_file_path: name,
          })
        }
        push('National ID', this.idNumber, this.docs.id)
        push('Driver Licence', this.licenseNumber, this.docs.license)
        push('Vehicle plate', this.plateNumber, this.docs.ownership || this.docs.license)
        push('Ownership certificate', this.plateNumber, this.docs.ownership)
        await axios.post('/drivers/save-driver-info', {
          submit: true,
          first_name: this.firstName,
          middle_name: this.middleName,
          last_name: this.lastName,
          phone_number: PhoneAuth.digits(this.phoneLocal),
          address: this.address || 'Addis Ababa',
          email: this.email || undefined,
          license_number: this.licenseNumber,
          plate_number: this.plateNumber,
          school_id: schoolId,
          documents,
        })
        this.done = true
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.message || (e.response.data.error && e.response.data.error[0]))) || e.message
      } finally {
        this.busy = false
      }
    },
  },
}
</script>
<style scoped>
.page { padding: 48px 0 80px; font-family: var(--font); }
.public-wrap { width: min(640px, calc(100% - 32px)); margin: 0 auto; }
.auth-box {
  background: var(--white);
  color: var(--navy);
  border-radius: 24px;
  padding: 32px;
  box-shadow: var(--shadow);
}
h1 { color: var(--navy); margin: 0 0 8px; }
.lead, .step-label { color: var(--slate); }
.step-label { text-transform: uppercase; letter-spacing: .08em; font-size: 12px; font-weight: 700; }
.phone-row { display: flex; gap: 10px; align-items: center; }
.phone-row span { background: var(--canvas); border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; font-weight: 700; }
label { display: block; margin: 12px 0 6px; font-size: 13px; color: var(--slate); font-weight: 600; }
</style>
